"use client";

import api from '@/app/api';
import HotelLoading from '@/components/frontend-components/hotel-loading';
import useAuthStore from '@/lib/useAuthStore';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState, useCallback } from 'react';

export default function AuthWrapper({ children }: { children: React.ReactNode }) {
  const { setAuthenticationStatus, logOutUser, isAuthenticated, getUser } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [localAuthenticated, setLocalAuthenticated] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  // Memoize the sessionExpiration handler to prevent recreating it on every render
  const handleSessionExpiration = useCallback(async () => {
    console.log("Session expiration detected");
    
    // First set authenticated state to false to prevent rendering protected components
    setLocalAuthenticated(false);
    
    try {
      // Clean up auth state
      await logOutUser();
    } catch (error) {
      console.error("Error during logout:", error);
      // Fallback cleanup if logOutUser fails
      localStorage.removeItem('access_token');
      setAuthenticationStatus(false);
    }
    
    // Only redirect if not already on login-related pages
    if (!pathname?.includes('/login') && 
        !pathname?.includes('/forgot-password')) {
      // Use location.href for a full page refresh to clean state
      window.location.href = '/login';
    }
  }, [logOutUser, pathname, setAuthenticationStatus]);

  // Check for session_expired flag in sessionStorage
  useEffect(() => {
    const checkSessionExpired = () => {
      const sessionExpired = sessionStorage.getItem('session_expired');
      if (sessionExpired === 'true') {
        // Clear the flag immediately to prevent multiple triggers
        sessionStorage.removeItem('session_expired');
        handleSessionExpiration();
        return true; // Signal we're handling expiration
      }
      return false;
    };
    
    // Check on mount
    const isExpired = checkSessionExpired();
    if (isExpired) return; // Skip other checks if we're handling expiration
    
    // Set interval to periodically check
    const interval = setInterval(checkSessionExpired, 2000);
    
    // Clean up interval on unmount
    return () => clearInterval(interval);
  }, [handleSessionExpiration]);

  // Main auth check effect
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const accessToken = localStorage.getItem('access_token');
        console.log("accessToken", accessToken);
        if (!accessToken) {
          await api.post('/users/logout', {}, {
            withCredentials: true
          });
          setAuthenticationStatus(false);
          setLocalAuthenticated(false);
          await handleSessionExpiration();
          // Only redirect if not on auth-related pages and not during initial server-side render
          if (typeof window !== 'undefined' && 
              !pathname?.includes('/login') && 
              !pathname?.includes('/forgot-password')) {
            router.replace('/login');
          }
          
          setLoading(false);
          return;
        }

        // Attempt to get user data to verify authentication
        await getUser();
        setLocalAuthenticated(true);
        
        setLoading(false);
      } catch (error) {
        console.error("Auth check failed:", error);
        
        // Handle authentication failure
        localStorage.removeItem('access_token');
        setLocalAuthenticated(false);
        setAuthenticationStatus(false);
        // Set session expired flag
        sessionStorage.setItem('session_expired', 'true');
        
        setLoading(false);
      }
    };

    checkAuth();
  }, [getUser, router, pathname, setAuthenticationStatus]);

  // Prevent flash of protected content
  if (loading) {
    return (
      <div className='w-full h-screen flex justify-center items-center'>
        <HotelLoading />
      </div>
    );
  }

  // The isAuthenticated from the store and our local state MUST agree
  // If they don't, we're in an inconsistent state
  if (localAuthenticated !== isAuthenticated) {
    console.warn("Authentication state mismatch, syncing...");
    setLocalAuthenticated(isAuthenticated); // Sync to store state
    
    // If we're rapidly switching states, show loading
    return (
      <div className='w-full h-screen flex justify-center items-center'>
        <HotelLoading />
      </div>
    );
  }

  return localAuthenticated ? children : null;
}

// export function isAuth(Component: React.FC) {
//   return function AuthenticatedComponent(props: any) {
//     const { setAuthenticationStatus }: any = useAuthStore();
//     const [loading, setLoading] = useState(true);
//     const [authenticated, setAuthenticated] = useState(false);
//     const router = useRouter();
//     const dispatch = useDispatch();

//     useEffect(() => {
//       const checkAuth = async () => {
//         try {
//           const accessToken = localStorage.getItem('access_token');
          
//           if (!accessToken) {
//             throw new Error('No access token');
//           }

//           // Make sure the Authorization header is set correctly
//           const response = await api.get('/users/me');

//           if (response.data) {
//             setAuthenticated(true);
//             setAuthenticationStatus(true);
//             dispatch(setUser(response.data));
//           } else {
//             throw new Error('Invalid response');
//           }
//         } catch (error:any) {
//           console.error('Auth check failed:', error);
//           setAuthenticationStatus(false);
//           setAuthenticated(false);
//           // Only remove token if it's an authentication error
//           if (error.response?.status === 401) {
//             localStorage.removeItem('access_token');
//           }
//           router.push('/login');
//         } finally {
//           setLoading(false);
//         }
//       };

//       checkAuth();
//     }, [router, dispatch, setAuthenticationStatus]);

//     if (loading) {
//       return (
//         <div className='w-[100vw] h-[100%] flex justify-center items-center font-bold text-2xl'>
//           <HotelLoading />
//         </div>
//       );
//     }

//     if (!authenticated) {
//       return null;
//     }

//     return <Component {...props} />;
//   };
// }